import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Save, Trash2, Check, X, FolderOpen, Sparkles, Star, Wifi } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useGameStore from '../../store/gameStore';
import useSaveStore from '../../store/saveStore';
import { formatMoney } from '../../utils/format';
import { useTranslation } from '../../i18n';
import { getTelemetryStatus } from '../../hooks/telemetryTransport';
import useLicenseStore from '../../store/licenseStore';

// 17/07/2026 — indicateur de synchro supervision (diagnostic apprenants
// « invisibles » côté formateur/admin). 18/07/2026 : + clé utilisée (masquée).
const TelemetrySyncLine = () => {
  const status = getTelemetryStatus();
  const licenseKey = useLicenseStore((st) => st.licenseKey);
  const maskedKey = licenseKey ? `···${licenseKey.slice(-6)}` : null;
  let label; let cls;
  if (!status || !status.lastAttemptAt) {
    label = 'Synchro supervision : en attente du premier envoi';
    cls = 'text-slate-500';
  } else if (status.lastResult === 'ok') {
    const mins = Math.max(0, Math.round((Date.now() - (status.lastSuccessAt || status.lastAttemptAt)) / 60000));
    label = `Synchro supervision : OK — il y a ${mins} min`;
    cls = 'text-emerald-400';
  } else if (status.lastResult === 'rejected') {
    // 19/07/2026 — messages distincts par cause (cas Timothée : un 422 de
    // format affichait à tort « clé non reconnue »).
    const r = status.reason || '';
    if (r === 'unknown_license') {
      label = 'Synchro supervision : clé de licence non reconnue par le serveur — vérifiez la clé avec votre formateur';
    } else if (r === 'inactive_license') {
      label = 'Synchro supervision : licence désactivée — contactez votre formateur';
    } else if (r.startsWith('http_')) {
      label = `Synchro supervision : données refusées par le serveur (code ${r.replace('http_', '')}) — signalez ce code à votre formateur`;
    } else {
      label = 'Synchro supervision : envoi refusé par le serveur — signalez-le à votre formateur';
    }
    cls = 'text-rose-400';
  } else {
    label = 'Synchro supervision : échec réseau — nouvelle tentative automatique';
    cls = 'text-amber-400';
  }
  return (
    <p className={`text-[10px] ${cls} flex items-center gap-1.5`} data-testid="telemetry-sync-status">
      <Wifi className="w-3 h-3 flex-shrink-0" /> {label}
      {maskedKey && <span className="text-slate-500 font-mono" data-testid="telemetry-sync-key">· clé {maskedKey}</span>}
    </p>
  );
};

export const SaveGameModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const gameState = useGameStore();
  const gameStarted = useGameStore(s => s.gameStarted);
  const { saveGame, deleteSave, getSlots, loadGame, startNewGameFresh, setFavoriteSlot, favoriteSlot, favoriteNote, setFavoriteNote } = useSaveStore();
  const navigate = useNavigate();
  const [savedSlot, setSavedSlot] = useState(null);
  const [loadingSlot, setLoadingSlot] = useState(null);
  const [error, setError] = useState(null);

  if (!open) return null;

  const slots = getSlots();

  const handleSave = (slotKey) => {
    saveGame(slotKey, gameState);
    setSavedSlot(slotKey);
    setError(null);
    setTimeout(() => setSavedSlot(null), 1500);
  };

  const handleLoad = (slotKey) => {
    if (!window.confirm('Charger cette sauvegarde ? La partie en cours sera remplacée.')) return;
    setLoadingSlot(slotKey);
    const res = loadGame(slotKey);
    if (res.ok) {
      setError(null);
      // Close modal and route back to the dashboard so the loaded game is visible.
      setTimeout(() => {
        onClose();
        navigate('/');
      }, 400);
    } else {
      setError(res.reason || 'Erreur');
      setLoadingSlot(null);
    }
  };

  const handleNewGame = () => {
    if (!window.confirm('Démarrer une NOUVELLE partie ? La partie en cours sera perdue (les 3 sauvegardes sont conservées).')) return;
    startNewGameFresh();
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-ink-800 rounded-xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()} data-testid="save-modal">
        <div className="px-5 py-4 border-b border-ink-600 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
            <Save className="w-5 h-5 text-foreground" /> Sauvegardes — 3 emplacements
          </h2>
          <button aria-label="Fermer" onClick={onClose} className="p-1 hover:bg-ink-700/60 rounded-lg" data-testid="save-modal-close">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
          {slots.map(({ key, save }) => (
            <div
              key={key}
              className="border border-ink-600 rounded-lg p-4 hover:border-accent transition-colors"
              data-testid={`save-slot-${key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Emplacement {key.replace('slot', '')}
                </span>
                {save && (
                  <div className="flex items-center gap-2">
                    <button aria-label={favoriteSlot === key
                        ? 'Sauvegarde favorite — mise en avant pour votre formateur (cliquer pour retirer)'
                        : 'Définir comme sauvegarde favorite (mise en avant pour votre formateur)'}
                      onClick={() => setFavoriteSlot(key)}
                      className="transition-colors"
                      data-testid={`favorite-slot-${key}`}
                      title={favoriteSlot === key
                        ? 'Sauvegarde favorite — mise en avant pour votre formateur (cliquer pour retirer)'
                        : 'Définir comme sauvegarde favorite (mise en avant pour votre formateur)'}
                    >
                      <Star className={`w-3.5 h-3.5 ${favoriteSlot === key ? 'fill-amber-400 text-amber-400' : 'text-slate-500 hover:text-amber-300'}`} />
                    </button>
                    <button aria-label="Supprimer cette sauvegarde"
                      onClick={() => deleteSave(key)}
                      className="text-slate-500 hover:text-danger transition-colors"
                      data-testid={`delete-slot-${key}`}
                      title="Supprimer cette sauvegarde"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>

              {save ? (
                <div className="mb-3">
                  <p className="text-sm font-medium text-slate-200">{save.name}</p>
                  <p className="text-[10px] text-slate-500">
                    Jour {save.totalDays} — {formatMoney(save.treasury)} — Phase {save.phase}
                  </p>
                  <p className="text-[10px] text-slate-500">
                    {new Date(save.date).toLocaleDateString('fr-FR')} à {new Date(save.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                  {/* 18/07/2026 — note pédagogique jointe au slot favori,
                      visible par le formateur en supervision. */}
                  {favoriteSlot === key && (
                    <div className="mt-2">
                      <input
                        type="text"
                        value={favoriteNote}
                        onChange={(e) => setFavoriteNote(e.target.value)}
                        maxLength={140}
                        placeholder="Note pour votre formateur (ex. : ma meilleure stratégie frigo)"
                        className="w-full text-[11px] bg-ink-900/60 border border-amber-500/40 rounded-md px-2 py-1.5 text-amber-100 placeholder:text-slate-600 focus:outline-none focus:border-amber-400"
                        data-testid="favorite-note-input"
                      />
                      <p className="text-[10px] text-slate-600 mt-0.5 text-right">{favoriteNote.length}/140 — visible par votre formateur</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-slate-500 mb-3 italic">Vide</p>
              )}

              <div className="flex gap-2">
                <button
                  onClick={() => handleSave(key)}
                  disabled={!gameStarted}
                  className={`flex-1 text-xs py-2 rounded-md font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed ${
                    savedSlot === key
                      ? 'bg-money/100 text-white'
                      : 'bg-accent text-white hover:bg-accent-hover'
                  }`}
                  data-testid={`save-to-${key}`}
                  title={gameStarted ? 'Sauvegarder' : 'Aucune partie en cours'}
                >
                  {savedSlot === key ? (
                    <span className="flex items-center justify-center gap-1"><Check className="w-3.5 h-3.5" /> {t('saved')}</span>
                  ) : (
                    <span className="flex items-center justify-center gap-1"><Save className="w-3.5 h-3.5" /> {save ? 'Écraser' : 'Sauver'}</span>
                  )}
                </button>
                <button
                  onClick={() => handleLoad(key)}
                  disabled={!save || loadingSlot === key}
                  className="flex-1 text-xs py-2 rounded-md font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1"
                  data-testid={`load-from-${key}`}
                >
                  <FolderOpen className="w-3.5 h-3.5" />
                  {loadingSlot === key ? '...' : 'Charger'}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="px-5 py-4 border-t border-ink-600 bg-ink-900/40">
          <TelemetrySyncLine />
          <button
            onClick={handleNewGame}
            className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-rose-600/20 hover:bg-rose-600/40 border border-rose-500/40 text-rose-200 hover:text-rose-100 transition-colors text-xs font-medium"
            data-testid="btn-new-game-fresh"
          >
            <Sparkles className="w-4 h-4" />
            Démarrer une nouvelle partie (reset complet)
          </button>
          <p className="text-[10px] text-slate-500 mt-2 text-center">
            La nouvelle partie repart d'une page blanche — aucune influence de la partie précédente. Vos sauvegardes restent intactes.
          </p>
        </div>

        {error && (
          <div className="mx-5 mb-4 p-2 rounded bg-rose-900/40 border border-rose-700 text-rose-200 text-xs" data-testid="save-modal-error">
            {error}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default SaveGameModal;
